/**
 * Compute node Hosting Plans under the Vendor Hosting Agreement Rev 2.10 §2.
 * Plans are hardware-independent: they differ only in the fixed monthly base
 * and the cap on the monthly network bonus, in USD per node.
 */
export const HOSTING_PLANS = [`core`, `plus`, `max`] as const

export type HostingPlan = (typeof HOSTING_PLANS)[number]

/** Hosting Agreement Rev 2.10 §2: per node, per month, in USD. A future revision changes this one place. */
export const HOSTING_PLAN_TERMS: Readonly<
  Record<
    HostingPlan,
    { readonly monthlyBase: number; readonly monthlyBonusCap: number }
  >
> = {
  core: { monthlyBase: 200, monthlyBonusCap: 150 },
  plus: { monthlyBase: 300, monthlyBonusCap: 300 },
  max: { monthlyBase: 400, monthlyBonusCap: 400 },
}

/** Each plan's name as Hosting Agreement Rev 2.10 §2 and Exhibit A print it. */
export const HOSTING_PLAN_NAMES: Readonly<Record<HostingPlan, string>> = {
  core: `Core`,
  plus: `Plus`,
  max: `Max`,
}

/** Hosting Agreement Rev 2.10: the base runs for a 30-year service term from activation. */
export const HOSTING_TERM_YEARS = 30

/**
 * The last resort: the plan a node gets when PowurOS sends none and its name
 * carries no recognized model (`equipmentDefaultHostingPlan`).
 */
export const FALLBACK_HOSTING_PLAN: HostingPlan = `max`

/**
 * The plan a node defaults to by its RTX PRO model when PowurOS sends none
 * (#5489). A plan PowurOS does send always wins.
 */
export const EQUIPMENT_MODEL_HOSTING_PLANS: Readonly<
  Record<string, HostingPlan>
> = {
  "4500": `core`,
  "5000": `plus`,
  "6000": `max`,
}

/** A model number standing as its own token: no letter, digit or decimal point either side. */
const EQUIPMENT_MODEL_TOKEN = /(?<![\p{L}\p{N}.])(\d{4})(?![\p{L}\p{N}]|\.\d)/gu

/**
 * The default plan for a Computer from its PowurOS equipment name, such as
 * `PRO 5000 72GB / 70%` or `Compute RTX Pro 6000`. The model number must stand
 * as a whole token, so `72GB` or `45000` never match. A name with no known
 * model, or with two different ones, has no default.
 */
export const equipmentDefaultHostingPlan = (
  name: unknown,
): HostingPlan | null => {
  if (typeof name !== `string`) return null
  const plans = new Set<HostingPlan>()
  for (const [, model] of name.matchAll(EQUIPMENT_MODEL_TOKEN)) {
    const plan = model ? EQUIPMENT_MODEL_HOSTING_PLANS[model] : undefined
    if (plan) plans.add(plan)
  }
  const [plan, ...others] = plans

  return plan && others.length === 0 ? plan : null
}

/** A `computerData.hostingPlan` value as a plan, case-insensitive; anything else reads as none. */
export const parseHostingPlan = (value: unknown): HostingPlan | null => {
  if (typeof value !== `string`) return null
  const normalized = value.trim().toLowerCase()

  return (HOSTING_PLANS as readonly string[]).includes(normalized)
    ? (normalized as HostingPlan)
    : null
}
